<template>
  <div class="virtual-tree">
    <div v-if="filterable"
         class="virtual-tree__search">
      <i class="mdi mdi-magnify"></i>
      <input v-model="searchText"
             type="text"
             :placeholder="searchPlaceholder" />
    </div>

    <div v-if="loading"
         class="virtual-tree__loading">
      <loading />
    </div>

    <div v-else-if="visibleRows.length"
         ref="viewport"
         class="virtual-tree__viewport"
         :style="{ maxHeight: `${maxVisibleRows * itemHeight}px` }"
         @scroll="onScroll">
      <div class="virtual-tree__sizer"
           :style="{ height: `${visibleRows.length * itemHeight}px` }">
        <div class="virtual-tree__window"
             :style="{ transform: `translateY(${startIndex * itemHeight}px)` }">
          <div v-for="row in windowRows"
               :key="row.node.key"
               class="virtual-tree__row"
               :class="{ 'virtual-tree__row--disabled': row.node.disabled }"
               :style="{
                 height: `${itemHeight}px`,
                 paddingLeft: `${row.level * indentSize}px`,
               }"
               @click="onSelect(row)">

            <button v-if="row.hasChildren"
                    type="button"
                    class="virtual-tree__chevron"
                    :aria-label="row.expanded ? 'Recolher' : 'Expandir'"
                    @click.stop="toggleExpand(row.node.key)">
              <i :class="`mdi ${row.expanded ? 'mdi-chevron-down' : 'mdi-chevron-right'}`"></i>
            </button>
            <span v-else
                  class="virtual-tree__chevron virtual-tree__chevron--empty"></span>

            <slot name="row"
                  :row="row" />
          </div>
        </div>
      </div>
    </div>

    <p v-else
       class="virtual-tree__empty">{{ emptyMessage }}</p>
  </div>
</template>

<script>
import Loading from "@components/generic/Loading";
import { childNodesOf, indexFlatTree } from "@packages/util/tree/flat-tree";
import { debounce } from "lodash";

const OVERSCAN = 10;
const DIACRITICS = new RegExp("[\\u0300-\\u036f]", "g");

function normalizeSearch(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(DIACRITICS, "");
}

export default {
  components: {
    Loading,
  },

  props: {
    nodes: {
      type: Array,
      default() {
        return [];
      },
    },
    selection: {
      type: Map,
      default() {
        return new Map();
      },
    },
    nodeIndex: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    filterable: {
      type: Boolean,
      default: true,
    },
    searchPlaceholder: {
      type: String,
      default: "Buscar...",
    },
    emptyMessage: {
      type: String,
      default: "Nenhum resultado encontrado",
    },
    itemHeight: {
      type: Number,
      default: 36,
    },
    maxVisibleRows: {
      type: Number,
      default: 10,
    },
    indentSize: {
      type: Number,
      default: 16,
    },
    searchDebounce: {
      type: Number,
      default: 250,
    },
  },

  data() {
    return {
      expanded: {},
      searchText: "",
      debouncedSearch: "",
      scrollTop: 0,
      viewportHeight: 0,
      scrollFrame: null,
      expandedBeforeSearch: null,
      lastSearchTerm: "",
    };
  },

  computed: {
    treeIndex() {
      return this.nodeIndex || indexFlatTree(this.nodes);
    },

    normalizedByKey() {
      const map = new Map();

      this.nodes.forEach((node) => {
        const label = normalizeSearch(node.label);
        const extra = node.searchText ? ` ${normalizeSearch(node.searchText)}` : "";
        map.set(node.key, label + extra);
      });

      return map;
    },

    searchResult() {
      const term = normalizeSearch(this.debouncedSearch);
      if (!term) return null;

      const keep = new Set();
      const expand = new Set();

      const direct = this.nodes.filter((node) => {
        const normalized = this.normalizedByKey.get(node.key);
        return normalized && normalized.includes(term);
      });

      const stack = direct.slice();
      while (stack.length) {
        const current = stack.pop();
        if (keep.has(current.key)) continue;
        keep.add(current.key);

        const children = childNodesOf(this.treeIndex, current.key);
        if (children.length) expand.add(current.key);
        children.forEach((child) => stack.push(child));
      }

      direct.forEach((node) => {
        let parentKey = node.parentKey;

        while (parentKey && !expand.has(parentKey)) {
          keep.add(parentKey);
          expand.add(parentKey);
          const parent = this.treeIndex.nodeByKey.get(parentKey);
          parentKey = parent ? parent.parentKey : null;
        }
      });

      return { keep, expand };
    },

    visibleRows() {
      const rows = [];
      const filter = this.searchResult ? this.searchResult.keep : null;

      const walk = (node, level) => {
        if (filter && !filter.has(node.key)) return;

        const kids = childNodesOf(this.treeIndex, node.key).filter(
          (kid) => !filter || filter.has(kid.key)
        );
        const hasChildren = kids.length > 0;
        const expanded = !!this.expanded[node.key];
        const selected = this.selection.get(node.key);

        rows.push({
          node,
          level,
          hasChildren,
          expanded,
          checked: !!(selected && selected.checked),
          partialChecked: !!(selected && selected.partialChecked),
        });

        if (hasChildren && expanded) {
          kids.forEach((kid) => walk(kid, level + 1));
        }
      };

      this.treeIndex.roots.forEach((root) => walk(root, 0));

      return rows;
    },

    startIndex() {
      const first = Math.floor(this.scrollTop / this.itemHeight) - OVERSCAN;

      return Math.max(0, first);
    },

    windowRows() {
      const height = this.viewportHeight || this.maxVisibleRows * this.itemHeight;
      const count = Math.ceil(height / this.itemHeight) + OVERSCAN * 2;

      return this.visibleRows.slice(this.startIndex, this.startIndex + count);
    },
  },

  watch: {
    searchText(value) {
      this.applyDebouncedSearch(value);
      this.resetScroll();
    },

    nodes: {
      immediate: true,
      handler() {
        const pruned = {};

        Object.keys(this.expanded).forEach((key) => {
          if (this.expanded[key] && this.treeIndex.nodeByKey.has(key)) pruned[key] = true;
        });

        this.expanded = pruned;
      },
    },

    searchResult(result, previous) {
      const termChanged = this.debouncedSearch !== this.lastSearchTerm;
      this.lastSearchTerm = this.debouncedSearch;

      if (result && !previous) this.expandedBeforeSearch = this.expanded;

      if (result) {
        const seeded = termChanged ? {} : Object.assign({}, this.expanded);
        result.expand.forEach((key) => {
          seeded[key] = true;
        });
        this.expanded = seeded;
      } else if (this.expandedBeforeSearch) {
        this.expanded = this.expandedBeforeSearch;
        this.expandedBeforeSearch = null;
      }
    },

    "visibleRows.length"(length, previousLength) {
      if (length < previousLength) this.clampScroll();
    },
  },

  created() {
    this.applyDebouncedSearch = debounce((value) => {
      this.debouncedSearch = value;
    }, this.searchDebounce);
  },

  mounted() {
    this.measureViewport();
  },

  updated() {
    if (!this.viewportHeight) this.measureViewport();
  },

  beforeDestroy() {
    this.applyDebouncedSearch.cancel();
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
  },

  methods: {
    measureViewport() {
      const { viewport } = this.$refs;
      if (viewport) this.viewportHeight = viewport.clientHeight;
    },

    onScroll(event) {
      const { scrollTop } = event.target;

      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);

      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollTop = scrollTop;
        this.scrollFrame = null;
      });
    },

    resetScroll() {
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
      this.scrollTop = 0;

      this.$nextTick(() => {
        const { viewport } = this.$refs;
        if (viewport) viewport.scrollTop = 0;
      });
    },

    clampScroll() {
      const height = this.viewportHeight || this.maxVisibleRows * this.itemHeight;
      const maxScrollTop = Math.max(0, this.visibleRows.length * this.itemHeight - height);

      if (this.scrollTop <= maxScrollTop) return;

      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
      this.scrollTop = maxScrollTop;

      this.$nextTick(() => {
        const { viewport } = this.$refs;
        if (viewport) viewport.scrollTop = maxScrollTop;
      });
    },

    toggleExpand(key) {
      const next = Object.assign({}, this.expanded);

      if (next[key]) delete next[key];
      else next[key] = true;

      this.expanded = next;
    },

    onSelect(row) {
      if (row.node.disabled) return;

      this.$emit("toggle-select", row);
    },
  },
};
</script>

<style lang="scss" scoped>
.virtual-tree {
  &__search {
    position: relative;
    margin-bottom: 10px;

    i {
      position: absolute;
      top: 50%;
      left: 10px;
      transform: translateY(-50%);
      font-size: 18px;
      color: #9e9e9e;
    }

    input {
      width: 100%;
      padding: 8px 12px 8px 34px;
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      outline: none;
      font-size: 14px;
      color: #636363;

      &:focus {
        border-color: #3b86ff;
      }
    }
  }

  &__loading {
    display: flex;
    justify-content: center;
    padding: 20px 0;
  }

  &__viewport {
    overflow-y: auto;
    overflow-x: hidden;
  }

  &__sizer {
    position: relative;
  }

  &__window {
    will-change: transform;
  }

  &__row {
    display: flex;
    align-items: center;
    cursor: pointer;

    &--disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }

  &__chevron {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    padding: 0;
    border: none;
    background: none;
    outline: none;
    color: #707070;
    cursor: pointer;

    i {
      font-size: 20px;
    }

    &--empty {
      cursor: default;
    }
  }

  &__empty {
    margin: 0;
    padding: 20px 0;
    text-align: center;
    font-size: 14px;
    color: #9e9e9e;
  }
}
</style>
